import { Component, OnDestroy, OnInit } from "@angular/core";
import { AuthService } from "../../auth/auth.service";
import { Logger } from "../../common/logger";
import { DataService } from "../../data.service";
import { GtagService } from "../../gtag/gtag.service";
import { TracknumQueueItem, CargoFeaturesModel } from "../../model";
import { DateUtils } from "../../shared/date-utils";
import { PackageState } from "../../model/TracknumQueueItem";

@Component({
    selector: 'app-storage-panel',
    templateUrl: './storage-panel.component.html',
    styleUrls: ['./storage-panel.component.css'],
    standalone: false
})
export class StoragePanelComponent implements OnInit, OnDestroy {
    scanValue: string = "";
    tracknumbers: TracknumQueueItem[] = [];
    packageDate: any = new Date();
    tracksData: string = "";
    showDialog: boolean = false;
    logger: Logger = Logger.Instance;
    tracksChecked: boolean = false;
    lastRowNumber: number = 1;
    cargoFeatures?: CargoFeaturesModel;

    processingQueueIntervalId: any; // need for clearInterval call
    resendingQueueIntervalId: any; // need for clearInterval call

    cities: any;
    selectedCity: any = null;

    constructor(
        private authService: AuthService,
        private dataService: DataService,
        private gtag: GtagService,
    ) {
        this.authService.cargoFeatures().then((cargoFeatures: CargoFeaturesModel) => {
            this.cargoFeatures = cargoFeatures;
        });

        this.gtag.pageview2('Storage');
    }

    ngOnInit() {
        // const subscr = this.dataService.cargoStoragies().subscribe({
        //     next: (data) => {
        //         this.cities = [{ id: null, name: "--- All cities ---" }, ...data];
        //     },
        //     complete: () => {
        //         subscr.unsubscribe();
        //     },
        //     error: (err) => {
        //         subscr.unsubscribe();
        //     }
        // });

        // run tracks processing queue
        this.runTracksProcessingQueue(2000);
        // run resend processing queue
        this.runTracksResendQueue(30000);
    }

    ngOnDestroy(): void {
        if (this.processingQueueIntervalId) {
            clearInterval(this.processingQueueIntervalId);
        }

        if (this.resendingQueueIntervalId) {
            clearInterval(this.resendingQueueIntervalId);
        }
    }

    /**
     * Processig tracks with status WAITING and UNKNOWN
     * 
     * @param processingInterval 
     */
    runTracksProcessingQueue(processingInterval: number) {
        this.processingQueueIntervalId = setInterval(() => {
            this.tracknumbers.forEach((num) => {
                if (num.state === PackageState.waiting || num.state === PackageState.unknown) {
                    // send data to the server
                    this.logger.info('processing tracknum', `${num.tracknum}`);

                    // mark the track as being processed (to prevent it from being sent for processing again)
                    num.state = PackageState.processing;

                    const subscr = this.dataService.addStorageTrack(num.tracknum).subscribe({
                        next: (data) => {
                            if (data) {
                                // set status acording the result
                                if (data.result === 0 || data.result === 1 || data.result === 2) {
                                    num.state = PackageState.processed;
                                } else {
                                    num.state = PackageState.unknown;
                                }

                                num.userInfo!.client_code = data.client_code ? data.client_code : undefined;
                            }
                        },
                        error: (err) => {
                            subscr.unsubscribe();
                            if (err.message === 'already_received') {
                                num.state = PackageState.already_processed;
                                num.userInfo!.client_code = err.errData.value.client_code ? err.errData.value.client_code : undefined;
                            } else {
                                this.logger.error('scanTrack_error', err);
                                num.state = PackageState.unknown;
                            }
                        },
                        complete: () => {
                            subscr.unsubscribe();
                        }
                    });
                }
            })
        }, processingInterval);
    }

    /**
    * Change state of tracks with status PROCESSING to status WAITING. 
    * This needed to resend the tracks for processing again (reporcessing) 
    * 
    * @param processingInterval 
    */
    runTracksResendQueue(processingInterval: number) {
        this.resendingQueueIntervalId = setInterval(() => {
            this.tracknumbers.forEach((num) => {
                if (num.state === PackageState.processing) {
                    num.state = PackageState.waiting;
                }
            });
        }, processingInterval);
    }

    readBarcode(event: KeyboardEvent) {
        let char = event.code.replace(/Key|Digit|Numpad(.*)/g, '$1');
        switch (char) {
            case 'Minus':
                this.scanValue += '-';
                break;
            case 'Enter':
                if (this.scanValue.trim() !== '') {
                    this.tracknumbers.unshift({
                        rownum: this.lastRowNumber++,
                        tracknum: this.scanValue.trim(),
                        time: new Date(),
                        state: PackageState.waiting,
                        packageDate: DateUtils.formatDateFull(this.packageDate),
                        userInfo: {
                            client_code: undefined
                        }
                    });

                    this.scanValue = '';
                }
                break;
            default:
                this.scanValue += char;
        }


    }

    importShipment() {
        this.showDialog = true;
    }

    checkReset() {
        this.tracksChecked = false;
    }

    checkData() {
        const rawData = this.tracksData.replaceAll('"', '').split('\n');
        let newData = '[';
        let commaDelimiter = '';

        rawData.forEach(el => {
            if (el && el.length > 0) {
                const data = el.split('\t\t');
                if (data && Array.isArray(data)) {
                    if (data.length == 1) {
                        const tracknum = data[0].trim();
                        newData += `${commaDelimiter}{"tracknum": "${tracknum}", "weight": null}`;
                        commaDelimiter = ',\n';
                    } else if (data.length == 2) {
                        const tracknum = data[0].trim();
                        const weightStr = data[1].trim();

                        if (weightStr && weightStr.length > 0 && !isNaN(parseFloat(weightStr))) {
                            const weight = Math.floor(parseFloat(weightStr) * 1000);
                            newData += `${commaDelimiter}{"tracknum": "${tracknum}", "weight": ${weight}}`;
                            commaDelimiter = ',\n';
                        }
                    } else {
                        return;
                    }
                }


            }
        });

        newData += ']';

        if (newData != '[]') {
            this.tracksData = newData;
            this.tracksChecked = true;
        }

    }

    bulkImport() {
        this.checkData();

        const subscr = this.dataService.addStorageTracksBulk(
            JSON.parse(this.tracksData),
            DateUtils.unixTimestamp(this.packageDate),
            this.selectedCity
        ).subscribe({
            next: (data) => {
                if (data && Array.isArray(data)) {
                    data.forEach((track: { tracknum: string, result: number, client_code: string }) => {
                        this.tracknumbers.unshift({
                            rownum: this.lastRowNumber++,
                            tracknum: track.tracknum,
                            time: this.packageDate,
                            state: (track.result === 0 || track.result === 1 || track.result === 2) ? PackageState.processed : PackageState.unknown,
                            packageDate: DateUtils.formatDateFull(this.packageDate),
                            userInfo: { client_code: track.client_code },
                        });
                    });
                }
                this.checkReset();
            },
            error: (err) => {
                subscr.unsubscribe();
                this.checkReset();
                this.logger.error('bulkImport_error', err);
            },
            complete: () => {
                subscr.unsubscribe();
            }
        });

        this.cancelImport();
    }

    cancelImport() {
        this.tracksData = '';
        this.showDialog = false;
    }

    clearValue() {
        this.scanValue = "";
    }

    getStyleClass(state: PackageState) {
        switch (state) {
            case PackageState.waiting: return 'waiting'
            case PackageState.processing: return 'processing'
            case PackageState.processed: return 'received'
            case PackageState.already_processed: return 'alreadyReceived'
            default: return 'error'
        }
    }

    getStateName(state: PackageState): string {
        switch (state) {
            case PackageState.waiting:
                return "Waiting";
            case PackageState.processing:
                return "Processing";
            case PackageState.processed:
                return "Processed";
            case PackageState.already_processed:
                return "Already processed";
            default:
                return "Unknown status";
        }
    }

}